import React, { useState, useRef, useEffect } from 'react';
import { useTheme, darkBackgroundColors } from '../../contexts/ThemeContext';
import { Settings, Sun, Moon, RotateCcw, Palette, X } from 'lucide-react';
import { Button } from './button';

// Custom Slider Component
const Slider = ({ value, onChange, min = 0, max = 100, label, suffix = '%' }) => {
  const percentage = ((value - min) / (max - min)) * 100;
  
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <span className="text-sm font-mono text-muted-foreground">{value}{suffix}</span>
      </div>
      <div className="relative h-2 bg-secondary rounded-full">
        <div 
          className="absolute h-full bg-primary rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value))}
          className="absolute w-full h-full opacity-0 cursor-pointer"
        />
        <div 
          className="absolute w-4 h-4 bg-white border-2 border-primary rounded-full shadow-md -translate-y-1/2 top-1/2 transition-all"
          style={{ left: `calc(${percentage}% - 8px)` }}
        />
      </div>
    </div>
  );
};

// Color Picker Component with Swatches and Custom Input
const ColorPicker = ({ value, onChange, label, swatches }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [customColor, setCustomColor] = useState(value);
  const pickerRef = useRef(null);
  
  useEffect(() => {
    setCustomColor(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCustomColorChange = (e) => {
    const color = e.target.value;
    setCustomColor(color);
    onChange(color);
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">{label}</label>
      
      {/* Current Color Display */}
      <div className="relative" ref={pickerRef}>
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="w-full flex items-center gap-3 p-2 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors"
          data-testid="color-picker-button"
        >
          <div 
            className="w-8 h-8 rounded-md border border-border shadow-inner"
            style={{ backgroundColor: value }}
          />
          <span className="text-sm font-mono text-foreground flex-1 text-left">{value.toUpperCase()}</span>
          <Palette className="w-4 h-4 text-muted-foreground" />
        </button>
        
        {/* Color Picker Dropdown */}
        {showPicker && (
          <div className="absolute z-50 mt-2 p-4 bg-card border border-border rounded-xl shadow-xl w-full min-w-[280px]">
            {/* Swatches */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Preset Colors</p>
              <div className="grid grid-cols-5 gap-2">
                {swatches.map((swatch) => (
                  <button
                    key={swatch.id}
                    onClick={() => {
                      onChange(swatch.color);
                      setCustomColor(swatch.color);
                    }}
                    className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${
                      value === swatch.color ? 'border-primary ring-2 ring-primary/50' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: swatch.color }}
                    title={swatch.name}
                  />
                ))}
              </div>
            </div>
            
            {/* RGB Color Picker */}
            <div className="border-t border-border pt-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Custom Color</p>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={customColor}
                  onChange={handleCustomColorChange}
                  className="w-12 h-12 rounded-lg cursor-pointer border-0 bg-transparent"
                  style={{ padding: 0 }}
                />
                <input
                  type="text"
                  value={customColor.toUpperCase()}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                      setCustomColor(val);
                      if (val.length === 7) {
                        onChange(val);
                      }
                    }
                  }}
                  className="flex-1 px-3 py-2 text-sm font-mono bg-secondary rounded-lg border border-border focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="#000000"
                  maxLength={7}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Theme Settings Panel
const ThemeSettingsPanel = ({ isOpen, onClose }) => {
  const { 
    isDarkMode, 
    setDarkMode,
    glassTransparency,
    glassBlur,
    backgroundColor,
    updateGlassTransparency,
    updateGlassBlur,
    updateBackgroundColor,
    resetGlassSettings
  } = useTheme();
  
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div 
        className="fixed right-4 top-20 z-50 w-80 bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
        data-testid="theme-settings-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-secondary/50">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-semibold text-foreground">Theme Settings</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-secondary transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Light/Dark Mode Toggle */}
          <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
            <div className="flex items-center gap-3">
              {isDarkMode ? (
                <Moon className="w-5 h-5 text-indigo-400" />
              ) : (
                <Sun className="w-5 h-5 text-amber-500" />
              )}
              <span className="font-medium text-foreground">
                {isDarkMode ? 'Dark Mode' : 'Light Mode'}
              </span>
            </div>
            <button
              onClick={() => setDarkMode(!isDarkMode)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                isDarkMode ? 'bg-primary' : 'bg-muted'
              }`}
              data-testid="theme-mode-toggle"
            >
              <div 
                className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${
                  isDarkMode ? 'translate-x-6' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
          
          {/* Glass Morphism Settings (only visible in dark mode) */}
          {isDarkMode && (
            <>
              <div className="border-t border-border pt-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                  Glass Effect Settings
                </p>
                
                {/* Transparency Slider */}
                <div className="space-y-4">
                  <Slider
                    value={glassTransparency}
                    onChange={updateGlassTransparency}
                    min={0}
                    max={100}
                    label="Transparency"
                  />
                  
                  {/* Blur Slider */}
                  <Slider
                    value={glassBlur}
                    onChange={updateGlassBlur}
                    min={0}
                    max={100}
                    label="Glass Blur"
                  />
                </div>
              </div>
              
              {/* Background Color */}
              <div className="border-t border-border pt-4">
                <ColorPicker
                  value={backgroundColor}
                  onChange={updateBackgroundColor}
                  label="Background Color"
                  swatches={darkBackgroundColors}
                />
              </div>
              
              {/* Reset Button */}
              <div className="border-t border-border pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetGlassSettings}
                  className="w-full"
                  data-testid="reset-theme-settings"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset to Default
                </Button>
              </div>
            </>
          )}
          
          {/* Preview Box */}
          <div className="border-t border-border pt-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Preview
            </p>
            <div 
              className="relative h-24 rounded-lg overflow-hidden"
              style={{ 
                backgroundColor: isDarkMode ? backgroundColor : '#F8FAFC',
                backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ec6a3507-f3ff-46ed-bf60-38c4e580efd9/artifacts/u116accu_RisingSunOverPumpWithGreenry.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div 
                className="absolute inset-2 rounded-md flex items-center justify-center"
                style={{ 
                  backgroundColor: isDarkMode 
                    ? `rgba(${parseInt(backgroundColor.slice(1, 3), 16)}, ${parseInt(backgroundColor.slice(3, 5), 16)}, ${parseInt(backgroundColor.slice(5, 7), 16)}, ${0.95 - (glassTransparency / 100) * 0.80})`
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: isDarkMode ? `blur(${Math.round((glassBlur / 100) * 40)}px)` : 'blur(8px)'
                }}
              >
                <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Glass Card Preview
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ThemeSettingsPanel;
